import { useEffect, useId, useState } from "react";
import Tooltip from "@mui/material/Tooltip";
import { Info } from "lucide-react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "@/auth/AuthProvider";
import { SectionCard } from "@/base";
import { ConfirmDialog } from "@/base/ConfirmDialog";
import {
  buttonClass,
  TOUCH_SQUARE,
  TOUCH_TARGET,
} from "@/components/buttonStyles";
import { OwnerDialog } from "@/components/surety-account/OwnerDialog";
import { updateAccount, type AccountDetail } from "@/lib/brokerAccounts";
import { saveErrorLines } from "@/lib/accountRecordForm";
import {
  buildOwnerRemoval,
  buildOwnerSave,
  OWNER_RELATIONSHIP_HELP,
  OWNER_RELATIONSHIP_LABELS,
  ownerDraftFor,
  ownerRows,
  removalOf,
  type OwnerDraft,
  type OwnerRelationship,
  type OwnerRow,
} from "@/lib/accountOwners";

// BRK-S408 — the Surety account's Owners & principals tab: the people who sign
// the indemnity agreement, as PMS holds them.
//
// Every change is the existing partial `PUT /api/broker/accounts/{id}` with
// the WHOLE `personnel[]` (a person the PUT leaves out is removed, PMS-S551),
// and the table changes only from PMS's answer — a refused save keeps the
// dialog open with the reason, and a refused removal keeps the row.
// Read-only brokers (no `account:edit`) see the table and nothing else.

const CHIP: Record<OwnerRelationship, string> = {
  principal_owner:
    "bg-violet-100 text-violet-700 dark:bg-violet-400/15 dark:text-violet-300",
  owner: "bg-blue-100 text-blue-700 dark:bg-blue-400/15 dark:text-blue-300",
  spouse: "bg-brk-bg text-brk-dim",
  non_owner_indemnitor:
    "bg-amber-100 text-amber-700 dark:bg-amber-400/15 dark:text-amber-300",
};

const CELL = "block py-1 align-top lg:table-cell lg:px-3 lg:py-3";
/** The column name a card shows beside a value; the table has a header row. */
const CARD_LABEL = "text-xs text-brk-dim lg:hidden";
const LINK = `text-sm font-medium text-brk-accent hover:underline ${TOUCH_TARGET}`;

export function SuretyOwnersPanel({
  detail,
  canEdit,
  onUpdated,
}: {
  detail: AccountDetail;
  canEdit: boolean;
  onUpdated: (fresh: AccountDetail) => void;
}) {
  const { authedRequest } = useAuth();
  const captionId = useId();
  const rows = ownerRows(detail);

  const [draft, setDraft] = useState<OwnerDraft | null>(null);
  const [dialogErrors, setDialogErrors] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [removing, setRemoving] = useState<string | null>(null);
  const [removeBusy, setRemoveBusy] = useState(false);
  const [panelErrors, setPanelErrors] = useState<string[]>([]);

  function open(id: string | null) {
    setDraft(ownerDraftFor(detail, id));
    setDialogErrors([]);
    setPanelErrors([]);
  }

  // "Complete the owners’ details" lands here with `?person=` (BRK-S438):
  // open that person's dialog once. A person this table does not list is
  // ignored — the link names whoever PMS's blocker named.
  const [params] = useSearchParams();
  const focusId = params.get("person");
  const [focused, setFocused] = useState(false);
  useEffect(() => {
    if (focused || !canEdit || !focusId) return;
    setFocused(true);
    if (rows.some((r) => r.id === focusId)) open(focusId);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- once, on landing
  }, [focusId, canEdit]);

  async function save() {
    if (!draft) return;
    const { input, errors } = buildOwnerSave(detail, draft);
    if (!input) {
      setDialogErrors(errors);
      return;
    }
    setSaving(true);
    setDialogErrors([]);
    try {
      onUpdated(await updateAccount(authedRequest, detail.id, input));
      setDraft(null);
    } catch (err) {
      setDialogErrors(saveErrorLines(err, input));
      // Write-only: a refused save never echoes the SSN back (BRK-S434).
      setDraft((d) => d && { ...d, ssn: "" });
    } finally {
      setSaving(false);
    }
  }

  async function remove(id: string) {
    setRemoving(null);
    setPanelErrors([]);
    const { input, errors } = buildOwnerRemoval(detail, id);
    if (!input) {
      setPanelErrors(errors);
      return;
    }
    setRemoveBusy(true);
    try {
      onUpdated(await updateAccount(authedRequest, detail.id, input));
    } catch (err) {
      setPanelErrors(saveErrorLines(err, input));
    } finally {
      setRemoveBusy(false);
    }
  }

  const removal = removing ? removalOf(detail, removing) : null;
  const person = draft
    ? (detail.personnel ?? []).find((p) => p.id === draft.key)
    : undefined;

  return (
    <SectionCard
      title="Owners and indemnitors"
      action={
        canEdit && (
          <button
            type="button"
            onClick={() => open(null)}
            disabled={removeBusy}
            className={buttonClass({ size: "sm" })}
          >
            + Add owner or indemnitor
          </button>
        )
      }
    >
      <p id={captionId} className="mb-4 text-sm text-brk-dim">
        Everyone with 10% or more ownership in the business, plus any non-owner
        the client wants signing as a creditworthy guarantor.
      </p>

      {removeBusy && (
        <p role="status" className="mb-3 text-sm text-brk-dim">
          Removing…
        </p>
      )}
      {panelErrors.length > 0 && (
        <div role="alert" className="mb-3 text-sm text-brk-danger">
          {panelErrors.map((line) => (
            <p key={line}>{line}</p>
          ))}
        </div>
      )}

      {rows.length === 0 ? (
        <p className="text-sm text-brk-dim">No owners recorded yet.</p>
      ) : (
        // Cards below lg, a table from lg; the scroller is the last resort for
        // a long email at 1024px, so the page itself never scrolls sideways.
        // Each card labels its own values, so the header row is a table's only.
        <div className="lg:overflow-x-auto">
          <table
            role="table"
            aria-describedby={captionId}
            aria-label="Owners and indemnitors"
            className="block w-full text-sm lg:table"
          >
            <thead role="rowgroup" className="hidden lg:table-header-group">
              <tr role="row" className="text-left text-xs text-brk-dim">
                <th
                  role="columnheader"
                  scope="col"
                  className="px-3 py-2 font-medium"
                >
                  <span className="inline-flex items-center gap-1">
                    Relationship
                    <Tooltip title={OWNER_RELATIONSHIP_HELP}>
                      <button
                        type="button"
                        aria-label={`About relationships: ${OWNER_RELATIONSHIP_HELP}`}
                        className={`inline-flex h-5 w-5 items-center justify-center rounded-full text-brk-dim hover:bg-brk-bg hover:text-brk-text ${TOUCH_SQUARE}`}
                      >
                        <Info size={14} aria-hidden />
                      </button>
                    </Tooltip>
                  </span>
                </th>
                {[
                  "Name and title",
                  "Ownership",
                  "Legal address",
                  "SSN",
                  "Contact",
                ].map((h) => (
                  <th
                    key={h}
                    role="columnheader"
                    scope="col"
                    className="px-3 py-2 font-medium"
                  >
                    {h}
                  </th>
                ))}
                {canEdit && (
                  <th
                    role="columnheader"
                    scope="col"
                    className="px-3 py-2 font-medium"
                  >
                    Actions
                  </th>
                )}
              </tr>
            </thead>
            <tbody
              role="rowgroup"
              className="flex flex-col gap-3 lg:table-row-group"
            >
              {rows.map((row) => (
                <OwnerTableRow
                  key={row.id}
                  row={row}
                  canEdit={canEdit}
                  busy={removeBusy}
                  onEdit={() => open(row.id)}
                  onRemove={() => setRemoving(row.id)}
                />
              ))}
            </tbody>
          </table>
          <p className="mt-3 text-xs text-brk-dim">SSNs are hidden.</p>
        </div>
      )}

      {draft && (
        <OwnerDialog
          draft={draft}
          onChange={setDraft}
          pii={person?.pii ?? null}
          errors={dialogErrors}
          busy={saving}
          onCancel={() => setDraft(null)}
          onSave={() => void save()}
        />
      )}

      <ConfirmDialog
        open={removal !== null}
        question={
          removal
            ? removal.spouse
              ? `Remove ${removal.name}? Removing ${removal.name} also removes ${removal.spouse.name}.`
              : `Remove ${removal.name}?`
            : ""
        }
        confirmLabel="Remove"
        cancelLabel="Cancel"
        onConfirm={() => removing && void remove(removing)}
        onCancel={() => setRemoving(null)}
      />
    </SectionCard>
  );
}

function OwnerTableRow({
  row,
  canEdit,
  busy,
  onEdit,
  onRemove,
}: {
  row: OwnerRow;
  canEdit: boolean;
  busy: boolean;
  onEdit: () => void;
  onRemove: () => void;
}) {
  return (
    // Explicit roles: a row restyled as a card keeps its table semantics in
    // every browser.
    <tr
      role="row"
      aria-label={row.name}
      className="flex flex-col rounded-lg border border-brk-border p-3 lg:table-row lg:rounded-none lg:border-0 lg:border-t lg:p-0"
    >
      <td role="cell" className={CELL}>
        <span
          className={`inline-block whitespace-nowrap rounded px-2 py-0.5 text-xs font-medium ${CHIP[row.relationship]}`}
        >
          {OWNER_RELATIONSHIP_LABELS[row.relationship]}
        </span>
      </td>
      <td role="cell" className={`${CELL} min-w-0 break-words`}>
        <p className="font-semibold text-brk-text">{row.name}</p>
        {row.title && <p className="text-brk-dim">{row.title}</p>}
        {row.sub && <p className="text-xs text-brk-dim">{row.sub}</p>}
        {row.needsDetails && (
          <p className="mt-1 flex flex-wrap items-center gap-2 text-xs font-medium text-amber-700 dark:text-amber-300">
            <span>Details needed</span>
            {canEdit && (
              <button
                type="button"
                onClick={onEdit}
                disabled={busy}
                aria-label={`Fill out ${row.name}’s details`}
                className={LINK}
              >
                Fill out
              </button>
            )}
          </p>
        )}
      </td>
      <td role="cell" className={CELL}>
        <span className={CARD_LABEL}>Ownership </span>
        <span>{row.share}</span>
      </td>
      <td role="cell" className={`${CELL} min-w-0 break-words`}>
        <span className={CARD_LABEL}>Legal address </span>
        <span>{row.legalAddress ?? "—"}</span>
        {row.mailingAddress && (
          <p className="text-xs text-brk-dim">{`Mailing: ${row.mailingAddress}`}</p>
        )}
      </td>
      <td role="cell" className={`${CELL} whitespace-nowrap`}>
        <span className={CARD_LABEL}>SSN </span>
        <span>{row.ssn ?? "—"}</span>
      </td>
      <td role="cell" className={`${CELL} min-w-0 break-all`}>
        <span className={CARD_LABEL}>Contact </span>
        {row.email && <p>{row.email}</p>}
        {row.phone && <p className="whitespace-nowrap">{row.phone}</p>}
        {!row.email && !row.phone && <span>—</span>}
      </td>
      {canEdit && (
        <td role="cell" className={`${CELL} whitespace-nowrap`}>
          <div className="flex gap-3">
            <button
              type="button"
              onClick={onEdit}
              disabled={busy}
              aria-label={`Edit ${row.name}`}
              className={LINK}
            >
              Edit
            </button>
            <button
              type="button"
              onClick={onRemove}
              disabled={busy}
              aria-label={`Remove ${row.name}`}
              className={LINK}
            >
              Remove
            </button>
          </div>
        </td>
      )}
    </tr>
  );
}
